import React, { useState } from 'react';
import { theme } from 'antd';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';

interface SettingsRowShellProps {
  left: React.ReactNode;
  right: React.ReactNode;
}

/**
 * "Left content, right control, whole row highlights on hover" shell shared
 * by every row-style control in the redesigned Settings page (SettingsRow's
 * label+pill-edit, toggle rows, select rows). No hard divider between rows
 * on purpose -- the hover highlight is what separates them, per the creamy
 * pill mockup. Uses onMouseEnter/Leave state instead of a CSS :hover class
 * since this app doesn't have a global stylesheet convention for that (see
 * BackButton for the same pattern).
 */
const SettingsRowShell: React.FC<SettingsRowShellProps> = ({ left, right }) => {
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const [hovering, setHovering] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        display: 'flex',
        // Desktop/tablet: unchanged -- wrap onto two lines once `left`
        // hits its 160px floor. Mobile: never wrap -- with a narrow
        // viewport that floor was routinely hit by longer labels (e.g. an
        // "Editar" pill), which pushed `right` onto its own line, no longer
        // vertically centered against the label it belongs to. `left` gets
        // minWidth:0 instead so its own text wraps within its column
        // (letting it grow taller) rather than the whole row wrapping.
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        padding: '14px 16px',
        margin: '0 -16px',
        borderRadius: 14,
        background: hovering ? token.colorFillTertiary : 'transparent',
        transition: 'background 180ms ease',
      }}
    >
      <div style={{ minWidth: isMobile ? 0 : 160 }}>{left}</div>
      <div style={{ flexShrink: 0 }}>{right}</div>
    </div>
  );
};

export default SettingsRowShell;
